"use client";

/**
 * Mobile interstitial ad — a one-time full-screen modal on small
 * viewports. Dismissed by tapping outside, the close button, or the
 * banner itself (which counts as a click and routes to the destination).
 *
 * State:
 *   - We remember dismissal per banner-id in localStorage (`ad-interstitial-dismissed-{id}`),
 *     so the same creative doesn't reappear after the user closes it.
 *   - Renders nothing on lg+ screens.
 */
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useUserInteraction } from "@/hooks/use-user-interaction";
import { getAdSlotStyle } from "@/lib/ad-sizing";

interface Props {
  id: string;
  title: string;
  imageUrl: string;
  alt: string | null;
}

export function MobileInterstitial({ id, title, imageUrl, alt }: Props) {
  const interacted = useUserInteraction();
  const [open, setOpen] = useState(false);
  const slotStyle = getAdSlotStyle("MOBILE_INTERSTITIAL");

  useEffect(() => {
    if (!interacted) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia("(min-width: 1024px)").matches) return;
    const key = `ad-interstitial-dismissed-${id}`;
    if (localStorage.getItem(key)) return;
    // Wait a beat so it doesn't blow open as the page paints.
    const t = setTimeout(() => setOpen(true), 1400);
    return () => clearTimeout(t);
  }, [id, interacted]);

  function dismiss() {
    setOpen(false);
    try {
      localStorage.setItem(`ad-interstitial-dismissed-${id}`, String(Date.now()));
    } catch {
      // ignore quota errors
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-60 bg-black/70 flex items-center justify-center p-4 lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={alt ?? title}
      onClick={dismiss}
    >
      <div
        className="relative max-w-[360px] w-full rounded-2xl overflow-hidden bg-card shadow-2xl"
        style={slotStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Cerrar"
          className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>
        <a
          href={`/api/ads/go/${id}`}
          target="_blank"
          rel="noopener sponsored"
          className="block h-full w-full"
          onClick={dismiss}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={imageUrl} alt={alt ?? title} className="h-full w-full object-cover" />
        </a>
        <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider text-white bg-black/40 backdrop-blur-sm px-1.5 py-0.5 rounded">
          Anuncio
        </span>
      </div>
    </div>
  );
}
